import { z } from 'zod';

export const AFFILIATIONS = ['cnss', 'cnrps', 'none'] as const;
export type Affiliation = (typeof AFFILIATIONS)[number];

export const TEACHER_STATUSES = ['active', 'disabled'] as const;
export type TeacherStatus = (typeof TEACHER_STATUSES)[number];

const name = z.string().trim().min(1, 'teachers.errors.required').max(80);
const affiliationNumber = z.string().trim().min(1).max(32);

const TeacherObjectSchema = z.object({
  firstName: name,
  lastName: name,
  email: z.email('teachers.errors.email'),
  cin: z
    .string()
    .trim()
    .regex(/^\d{8}$/, 'teachers.errors.cin'),
  rib: z
    .string()
    .trim()
    .regex(/^\d{20}$/, 'teachers.errors.rib'),
  affiliation: z.enum(AFFILIATIONS),
  affiliationNumber: affiliationNumber.nullable().optional(),
  fiscalId: z
    .string()
    .trim()
    .max(32)
    .nullable()
    .optional()
    .or(z.literal('').transform(() => null)),
});

function refineAffiliation(
  v: { affiliation?: Affiliation; affiliationNumber?: string | null },
  ctx: z.RefinementCtx,
) {
  const number = v.affiliationNumber?.trim() || null;
  if (v.affiliation === 'none' && number) {
    ctx.addIssue({
      code: 'custom',
      path: ['affiliationNumber'],
      message: 'teachers.errors.affiliationNone',
    });
  }
  if (v.affiliation && v.affiliation !== 'none' && !number) {
    ctx.addIssue({
      code: 'custom',
      path: ['affiliationNumber'],
      message: 'teachers.errors.affiliationNumber',
    });
  }
}

/** Fiche enseignant. The same rules run in the form and in the API. */
export const TeacherFieldsSchema = TeacherObjectSchema.superRefine(refineAffiliation);
export type TeacherFields = z.input<typeof TeacherFieldsSchema>;

export const UpdateTeacherSchema = TeacherObjectSchema.partial().extend({
  status: z.enum(TEACHER_STATUSES).optional(),
});
export type UpdateTeacher = z.input<typeof UpdateTeacherSchema>;

export const TeacherSchema = z.object({
  id: z.uuid(),
  firstName: z.string(),
  lastName: z.string(),
  email: z.string().nullable(),
  status: z.enum(['active', 'invited', 'disabled']),
  cin: z.string().nullable(),
  rib: z.string().nullable(),
  affiliation: z.enum(AFFILIATIONS).nullable(),
  affiliationNumber: z.string().nullable(),
  fiscalId: z.string().nullable(),
  subjects: z.array(z.string()),
});
export type Teacher = z.infer<typeof TeacherSchema>;

export const TeacherListQuerySchema = z.object({ yearId: z.uuid() });
